import { BannerPriority, type BannerMessage } from '@/lib/global-banner';
import { couldNotUploadCopy, waitingToUploadCopy } from '@/lib/waiting-to-upload-copy';
import { INSTALL_GUIDE_URL } from '@/config/env';

/** Pages where only the update message applies: nobody is signed in to the app yet, or access is blocked. */
const AUTH_PATHS = ['/login', '/auth/callback', '/select-organization', '/offline-expired'];

export function isAuthRoute(pathname: string): boolean {
  return AUTH_PATHS.includes(pathname);
}

const SYNC_DETAILS = { label: 'See details', to: '/settings?tab=sync' };

export interface UnsyncedWorkState {
  /** Documents waiting to upload; 0 when they were not counted. */
  pendingCount: number;
  online: boolean;
  onDashboard: boolean;
  /** The last sync failed for a reason other than an expired session. */
  syncFailed: boolean;
  /** When that sync finished: a new failure re-shows a dismissed message. */
  failedAt: Date | null;
  /** The sync banner is showing progress right now. */
  syncingVisibly: boolean;
}

/**
 * One message for work that has not reached Mon Ami: a failed sync (any page)
 * or documents waiting to upload (dashboard). Whether the browser may delete
 * that work is a separate message (STORAGE_UNPROTECTED_MESSAGE).
 */
export function unsyncedWorkMessage(state: UnsyncedWorkState): BannerMessage | null {
  const { pendingCount, online, onDashboard, syncFailed, failedAt, syncingVisibly } = state;
  const base = { id: 'unsynced-work', priority: BannerPriority.unsyncedWork, severity: 'warning' as const, dismissible: true };

  if (syncFailed) {
    const failure = { ...base, action: SYNC_DETAILS, dismissKey: `failed:${failedAt?.toISOString() ?? ''}` };
    return pendingCount > 0
      ? { ...failure, text: couldNotUploadCopy(pendingCount) }
      : { ...failure, text: "We couldn't sync with Mon Ami just now. We'll keep trying automatically." };
  }

  if (pendingCount === 0 || syncingVisibly || !onDashboard) return null;
  return { ...base, text: waitingToUploadCopy(pendingCount, online) };
}

/**
 * Shown while the browser may delete the device's offline data on its own: the
 * app is not installed and the browser has not granted persistent storage.
 */
export const STORAGE_UNPROTECTED_MESSAGE: BannerMessage = {
  id: 'storage-unprotected',
  priority: BannerPriority.storageProtection,
  severity: 'warning',
  text: 'Install Mon Ami GO on this device to keep your offline work safe.',
  action: { label: 'How to install', href: INSTALL_GUIDE_URL },
};

export const SESSION_EXPIRED_MESSAGE: BannerMessage = {
  id: 'session-expired',
  priority: BannerPriority.sessionExpired,
  severity: 'critical',
  text: "Sign in again to upload your work. It's safe on this device until then.",
  action: { label: 'Sign in', to: '/login' },
};

/** `daysRemaining` before the offline limit blocks access. */
export function offlineLimitMessage(daysRemaining: number): BannerMessage {
  const urgent = daysRemaining < 1;
  let within: string;
  if (urgent) {
    const hours = Math.max(1, Math.floor(daysRemaining * 24));
    within = hours === 1 ? '1 hour' : `${hours} hours`;
  } else {
    const days = Math.floor(daysRemaining);
    within = days === 1 ? '1 day' : `${days} days`;
  }
  return {
    id: 'offline-limit',
    priority: BannerPriority.offlineLimit,
    severity: urgent ? 'critical' : 'warning',
    text: `Connect to the internet within ${within} to keep using Mon Ami GO offline.`,
    dismissible: !urgent,
  };
}

export const LOW_STORAGE_MESSAGE: BannerMessage = {
  id: 'low-storage',
  priority: BannerPriority.lowStorage,
  severity: 'critical',
  text: 'This device is almost out of space. Free some up so you can keep saving your work.',
  action: { label: 'Manage storage', to: '/settings?tab=storage' },
};

export function updateMessage(reloading: boolean, reload: () => void): BannerMessage {
  return {
    id: 'update',
    priority: BannerPriority.update,
    severity: 'info',
    text: 'A new version of Mon Ami GO is ready.',
    action: { label: reloading ? 'Reloading…' : 'Reload', onClick: reload, disabled: reloading },
  };
}
